// src/components/result/ResultStatGrid.js
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stat } from "./stat";

/**
 * items: Array<{
 *   label: string,
 *   value?: ReactNode,
 *   hint?: string,
 *   metric?: string,              // e.g. 'lungAge' | 'lungFitness' | 'respiratoryRate' | 'breathingRecovery' | 'recoveryTime' | 'recoveryCurve' | 'totalVolume'
 *   lockedValue?: ReactNode,      // optional override when locked (defaults to "")
 *   lockedHint?: string           // optional override when locked (defaults to t("common.locked"))
 * }>
 *
 * locks: Record<metric, boolean>
 */
export function ResultStatGrid({ items = [], locks = {}, className = "" }) {
  const { t } = useTranslation();

  // Back-compat: accept 'recoverTime' if it shows up anywhere
  const normLocks = useMemo(() => {
    if (!locks) return {};
    return {
      ...locks,
      recoveryTime:
        typeof locks.recoveryTime === "boolean"
          ? locks.recoveryTime
          : locks.recoverTime ?? locks.recoveryTime,
    };
  }, [locks]);

  return (
    <div className={`w-full h-fit flex flex-col items-center ${className}`}>
      <div className="flex flex-wrap gap-4 items-start justify-start">
        {items.map((s, i) => {
          const isLocked =
            s.metric &&
            Object.prototype.hasOwnProperty.call(normLocks, s.metric)
              ? !!normLocks[s.metric]
              : false;
          const value = isLocked ? s.lockedValue ?? "" : s.value ?? "";
          const hint = isLocked
            ? s.lockedHint ?? t("common.locked", { defaultValue: "Locked" })
            : s.hint;

          return (
            <Stat
              key={i}
              label={s.label}
              value={value}
              hint={hint}
              data-locked={isLocked ? "true" : "false"}
            />
          );
        })}
      </div>
    </div>
  );
}
